// Central SVG renderer for Lucide-based icons.
// Portiert aus public/js/organizer/event/modules/participants-v4/ui/components/Icons.js.
// Memoized (FIFO bounded) fuer hot paths wie Tabellen-Rerenders.

import { LUCIDE_PATHS, ICON_ALIASES } from './paths.js';

const DEFAULT_SIZE = 20;
const DEFAULT_STROKE = 2;
const MIN_SIZE = 8;
const MAX_SIZE = 128;
const CACHE_MAX = 512;
const _cache = new Map();

function escAttr(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function coerceSize(n) {
  if (typeof n === 'boolean') return DEFAULT_SIZE;
  const v = Number(n);
  if (!Number.isFinite(v)) return DEFAULT_SIZE;
  const i = Math.trunc(v);
  if (i < MIN_SIZE || i > MAX_SIZE) return DEFAULT_SIZE;
  return i;
}

// Attribute-value whitelist for the ``color`` option. Anything outside is
// rejected to ``currentColor`` so attacker-controlled strings cannot escape
// the attribute context. Accepts CSS named colors, hex, rgb/rgba/hsl/hsla and
// ``var(--token[, fallback])``.
const COLOR_ALLOW = /^(?:currentColor|[a-zA-Z]{3,24}|#[0-9a-fA-F]{3,8}|rgba?\([^)]{1,60}\)|hsla?\([^)]{1,60}\)|var\(--[a-zA-Z0-9_-]+(?:\s*,\s*[^)]{1,60})?\))$/;

function coerceColor(c) {
  if (typeof c !== 'string' || !c) return 'currentColor';
  return COLOR_ALLOW.test(c) ? c : 'currentColor';
}

function resolve(name) {
  if (typeof name !== 'string' || !name) return null;
  // hasOwnProperty-style guard — LUCIDE_PATHS/ICON_ALIASES are frozen plain
  // objects from paths.js, but defense in depth: never read inherited props.
  const lucide = Object.prototype.hasOwnProperty.call(ICON_ALIASES, name)
    ? ICON_ALIASES[name]
    : name;
  if (!Object.prototype.hasOwnProperty.call(LUCIDE_PATHS, lucide)) return null;
  return LUCIDE_PATHS[lucide];
}

/**
 * Render a Lucide icon as inline SVG markup string.
 * @param {string} name  - Alias (camelCase) or Lucide name (kebab-case).
 * @param {object} opts
 * @param {number} [opts.size=20]
 * @param {string} [opts.className='']
 * @param {number} [opts.strokeWidth=2]
 * @param {boolean} [opts.filled=false]
 * @returns {string} SVG markup, or '' for unknown names.
 */
export function icon(name, opts = {}) {
  const inner = resolve(name);
  if (inner === null) return '';
  const size = coerceSize(opts.size ?? DEFAULT_SIZE);
  // Accept both className and cls to mirror the Python helper signature.
  const rawCls = opts.className ?? opts.cls ?? '';
  const cls = rawCls ? escAttr(String(rawCls)) : '';
  let sw = opts.strokeWidth ?? DEFAULT_STROKE;
  if (typeof sw === 'boolean') sw = DEFAULT_STROKE;
  sw = Number(sw);
  if (!Number.isFinite(sw) || sw < 1 || sw > 4) sw = DEFAULT_STROKE;
  const filled = opts.filled === true || opts.filled === 1 || opts.filled === '1';
  const color = coerceColor(opts.color);

  const key = `${name}|${size}|${cls}|${sw}|${filled ? 1 : 0}|${color}`;
  const hit = _cache.get(key);
  if (hit !== undefined) return hit;

  const fill = filled ? color : 'none';
  const stroke = filled ? 'none' : color;
  const strokeAttr = filled ? '' : ` stroke-width="${sw}"`;
  const classAttr = cls ? ` class="cita-icon ${cls}"` : ' class="cita-icon"';
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" ` +
    `viewBox="0 0 24 24" fill="${fill}" stroke="${stroke}"${strokeAttr} ` +
    `stroke-linecap="round" stroke-linejoin="round"${classAttr} aria-hidden="true" focusable="false">${inner}</svg>`;

  if (_cache.size >= CACHE_MAX) {
    const firstKey = _cache.keys().next().value;
    if (firstKey !== undefined) _cache.delete(firstKey);
  }
  _cache.set(key, svg);
  return svg;
}

export function iconExists(name) {
  return resolve(name) !== null;
}

export function iconNames() {
  return [...new Set([...Object.keys(ICON_ALIASES), ...Object.keys(LUCIDE_PATHS)])].sort();
}

// Test hook — NOT part of public API.
export function _resetIconCache() {
  _cache.clear();
}
